import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { calculateProfileStrength, type ProfileStrengthRow } from '../lib/profileStrength';
import { useAuthStore } from '../store/authStore';

const PROFILE_UPDATED = 'golfauth-profile-updated';

/**
 * Loads the shared `profiles` row (same id as auth user) and returns 0–100 strength score.
 * Used for the amber “complete your profile” dot on golfer nav.
 */
export function useProfileStrengthScore(): number | undefined {
  const customer = useAuthStore((s) => s.customer);
  const location = useLocation();
  const [score, setScore] = useState<number | undefined>();

  const authId = customer?.auth_id ?? null;

  const fetchScore = useCallback(async () => {
    if (!authId) {
      setScore(undefined);
      return;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('name, email, phone, handicap, homeclub, country_code')
      .eq('id', authId)
      .maybeSingle();

    if (error || !data) {
      setScore(undefined);
      return;
    }
    setScore(calculateProfileStrength(data as ProfileStrengthRow).score);
  }, [authId]);

  useEffect(() => {
    void fetchScore();
  }, [fetchScore, location.pathname]);

  useEffect(() => {
    const onUpdated = () => void fetchScore();
    window.addEventListener(PROFILE_UPDATED, onUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED, onUpdated);
  }, [fetchScore]);

  return score;
}

export function notifyProfileStrengthUpdated(): void {
  window.dispatchEvent(new Event(PROFILE_UPDATED));
}
